import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  InputBase,
  Link,
  Paper,
  Popper,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { MultiSelectDropdownProps, OptionGroup } from './FilterBar.props';

const OptionRow: React.FC<{
  label: string;
  checked: boolean;
  onClick: () => void;
  accentColor: string;
  hoverBg: string;
}> = ({ label, checked, onClick, accentColor, hoverBg }) => (
  <Box
    role="option"
    aria-selected={checked}
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 1,
      py: 0.25,
      cursor: 'pointer',
      borderRadius: 0.5,
      '&:hover': { bgcolor: hoverBg },
    }}
  >
    <Checkbox
      checked={checked}
      size="small"
      tabIndex={-1}
      sx={{ p: 0.5, '&.Mui-checked': { color: accentColor } }}
    />
    <Typography variant="body2" sx={{ ml: 0.5 }}>{label}</Typography>
  </Box>
);

const GroupHeader: React.FC<{
  group: OptionGroup;
  selected: string[];
  onChange: (next: string[]) => void;
  accentColor: string;
}> = ({ group, selected, onChange, accentColor }) => {
  const allSelected = group.values.every((v) => selected.includes(v));

  const handleToggleGroup = () => {
    if (allSelected) {
      onChange(selected.filter((s) => !group.values.includes(s)));
    } else {
      const merged = new Set([...selected, ...group.values]);
      onChange(Array.from(merged));
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, pt: 1.25, pb: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888' }}>
        {group.name}
      </Typography>
      <Link
        component="button"
        variant="caption"
        onClick={handleToggleGroup}
        sx={{ color: accentColor, cursor: 'pointer', fontSize: '11px' }}
      >
        {allSelected ? 'Deselect' : 'Select all'}
      </Link>
    </Box>
  );
};

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
  groups,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);

  const filtered = useMemo(
    () => options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase())),
    [options, search],
  );

  const handleToggle = useCallback(
    (value: string) => {
      const next = selected.includes(value)
        ? selected.filter((s) => s !== value)
        : [...selected, value];
      onChange(next);
    },
    [selected, onChange],
  );

  const handleSelectAll = () => onChange(filtered.map((o) => o.value));
  const handleClear = () => onChange([]);

  const accent = theme.palette.v4.accent;
  const hoverBg = theme.palette.action.hover;

  const renderGroupedOptions = () => {
    if (!groups) return null;
    return groups.map((group) => {
      const groupOptions = filtered.filter((o) => group.values.includes(o.value));
      if (groupOptions.length === 0) return null;
      return (
        <Box key={group.name}>
          <GroupHeader group={group} selected={selected} onChange={onChange} accentColor={accent} />
          {groupOptions.map((opt) => (
            <OptionRow key={opt.value} label={opt.label} checked={selected.includes(opt.value)}
              onClick={() => handleToggle(opt.value)} accentColor={accent} hoverBg={hoverBg} />
          ))}
        </Box>
      );
    });
  };

  const renderFlatOptions = () =>
    filtered.map((opt) => (
      <OptionRow key={opt.value} label={opt.label} checked={selected.includes(opt.value)}
        onClick={() => handleToggle(opt.value)} accentColor={accent} hoverBg={hoverBg} />
    ));

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: 'relative' }}>
        <Button
          ref={anchorRef}
          onClick={() => setOpen((p) => !p)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`${label} filter`}
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.v4.filterInput,
            border: `1px solid ${theme.palette.v4.filterInputBorder}`,
            borderRadius: 1, px: 1, py: 0.3, minWidth: 120,
            justifyContent: 'space-between',
            color: theme.palette.text.primary,
            '&:hover': { bgcolor: theme.palette.v4.filterInput, borderColor: accent },
          }}
          endIcon={<ArrowDropDownIcon />}
        >
          <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>{label}</Typography>
          {selected.length > 0 && (
            <Box component="span" sx={{
              ml: 1, bgcolor: accent, color: theme.palette.v4.surface,
              borderRadius: '10px', px: 0.75, fontSize: '0.7rem', fontWeight: 600,
              minWidth: 18, textAlign: 'center', lineHeight: '18px',
            }}>
              {selected.length}
            </Box>
          )}
        </Button>

        <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: theme.zIndex.modal + 1 }}>
          <Paper elevation={4} sx={{ width: 260, maxHeight: 380, display: 'flex', flexDirection: 'column', mt: 0.5, border: `1px solid ${theme.palette.v4.border}` }}>
            <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
              <InputBase
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                inputProps={{ 'aria-label': `Search ${label}` }}
                sx={{ border: `1px solid ${theme.palette.v4.filterInputBorder}`, borderRadius: 1, px: 1, py: 0.25, fontSize: '0.85rem' }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, px: 1.5, pb: 1 }}>
              <Link component="button" variant="body2" onClick={handleSelectAll} sx={{ color: accent, cursor: 'pointer' }}>Select All</Link>
              <Link component="button" variant="body2" onClick={handleClear} sx={{ color: accent, cursor: 'pointer' }}>Clear</Link>
            </Box>

            <Box role="listbox" aria-label={`${label} options`} sx={{ overflowY: 'auto', maxHeight: 280, px: 0.5 }}>
              {groups ? renderGroupedOptions() : renderFlatOptions()}
              {filtered.length === 0 && (
                <Typography variant="body2" sx={{ px: 1.5, py: 1, color: theme.palette.text.secondary }}>No results</Typography>
              )}
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
